import React, { useEffect, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import './AdminPayouts.css';
import { API_BASE_URL } from '../../api';
import { getApiKey } from '../../utils/auth';

const AdminPayouts = () => {
  const [loading, setLoading] = useState(true);
  const [payouts, setPayouts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [status, setStatus] = useState('');
  const [vendor, setVendor] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [marking, setMarking] = useState(null);
  const [referenceMap, setReferenceMap] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [summary, setSummary] = useState(null);
  const [details, setDetails] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showDetails, setShowDetails] = useState(false);

  const load = async (opts = {}) => {
    try {
      setLoading(true);
      const qp = new URLSearchParams({
        status: opts.status ?? status,
        vendor: opts.vendor ?? vendor,
        date_from: opts.dateFrom ?? dateFrom,
        date_to: opts.dateTo ?? dateTo,
        page: String(opts.page ?? page),
        limit: String(opts.limit ?? limit)
      }).toString();
      const adminKey = getApiKey();
      const [listRes, sumRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/payouts?${qp}`, { headers: { 'x-admin-key': adminKey } }),
        fetch(`${API_BASE_URL}/api/admin/payouts/summary`, { headers: { 'x-admin-key': adminKey } })
      ]);
      if (listRes.ok) {
        const data = await listRes.json();
        setPayouts(data.items || []);
        setTotal(data.total || 0);
      }
      if (sumRes.ok) {
        const s = await sumRes.json();
        setSummary(s);
      }
    } catch (e) {
      console.error('Admin payouts load error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const fmt = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amt || 0);

  // Calculate payout metrics
  const payoutMetrics = {
    totalPayouts: payouts.length,
    totalAmount: payouts.reduce((sum, p) => sum + (p.amount || 0), 0),
    pendingPayouts: payouts.filter(p => p.status === 'pending').length,
    paidPayouts: payouts.filter(p => p.status === 'paid').length,
    failedPayouts: payouts.filter(p => p.status === 'failed').length,
    pendingAmount: payouts.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.amount || 0), 0),
    paidAmount: payouts.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0)
  };

  const StatusIcon = ({ status }) => {
    if (status === 'paid') return <LucideIcons.CheckCircle size={16} className="status-paid" />;
    if (status === 'pending') return <LucideIcons.Clock size={16} className="status-pending" />;
    return <LucideIcons.XCircle size={16} className="status-failed" />;
  };

  const markPaid = async (id) => {
    try {
      setMarking(id);
      const ref = referenceMap[id] || '';
      const adminKey = getApiKey();
      const res = await fetch(`${API_BASE_URL}/api/admin/payouts/${id}/mark-paid`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': adminKey },
        body: JSON.stringify({ reference: ref })
      });
      if (!res.ok) {
        const e = await res.json();
        alert(e.error || 'Failed to mark as paid');
      } else {
        await load();
      }
    } catch (e) {
      console.error('markPaid error:', e);
    } finally {
      setMarking(null);
    }
  };

  const toggleSelect = (id, checked) => {
    setSelectedIds(prev => checked ? Array.from(new Set([...prev, id])) : prev.filter(x => x !== id));
  };

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? payouts.map(p => p.id) : []);
  };

  const bulkMark = async (newStatus) => {
    try {
      if (selectedIds.length === 0) return;
      const adminKey = getApiKey();
      const res = await fetch(`${API_BASE_URL}/api/admin/payouts/bulk/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': adminKey },
        body: JSON.stringify({ ids: selectedIds, status: newStatus })
      });
      if (!res.ok) { const e = await res.json(); alert(e.error || 'Bulk update failed'); return; }
      setSelectedIds([]);
      await load();
    } catch (e) {
      console.error('bulkMark error:', e);
    }
  };

  const openDetails = async (id) => {
    try {
      const adminKey = getApiKey();
      const [dRes, lRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/payouts/${id}`, { headers: { 'x-admin-key': adminKey } }),
        fetch(`${API_BASE_URL}/api/admin/payouts/${id}/logs`, { headers: { 'x-admin-key': adminKey } })
      ]);
      if (dRes.ok) setDetails(await dRes.json()); else setDetails(null);
      if (lRes.ok) setLogs(await lRes.json()); else setLogs([]);
      setShowDetails(true);
    } catch (e) {
      console.error('openDetails error:', e);
    }
  };

  const downloadReceipt = async (id) => {
    try {
      const adminKey = getApiKey();
      const res = await fetch(`${API_BASE_URL}/api/admin/payouts/${id}/receipt`, { headers: { 'x-admin-key': adminKey } });
      if (!res.ok) { const e = await res.json(); alert(e.error || 'Receipt download failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `payout-${id}-receipt.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('downloadReceipt error:', e);
    }
  };

  return (
    <div className="admin-payouts">
      <div className="payouts-header">
        <div className="payouts-header-content">
          <div className="payouts-title-section">
            <h1 className="payouts-title">Payout Management</h1>
            <p className="payouts-subtitle">Manage vendor payouts, track payments, and monitor financial transactions</p>
          </div>
          <div className="payouts-actions">
            <button className="payouts-action-btn primary">
              <LucideIcons.Plus size={16} />
              New Payout
            </button>
            <button className="payouts-action-btn secondary">
              <LucideIcons.Download size={16} />
              Export Data
            </button>
          </div>
        </div>
        
        <div className="payouts-tabs">
          <button 
            className={`payouts-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <LucideIcons.LayoutDashboard size={18} />
            <span>Overview</span>
          </button>
          <button 
            className={`payouts-tab ${activeTab === 'payouts' ? 'active' : ''}`}
            onClick={() => setActiveTab('payouts')}
          >
            <LucideIcons.IndianRupee size={18} />
            <span>All Payouts</span>
          </button>
          <button 
            className={`payouts-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <LucideIcons.Clock size={18} />
            <span>Pending</span>
          </button>
          <button 
            className={`payouts-tab ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
          >
            <LucideIcons.BarChart3 size={18} />
            <span>Reports</span>
          </button>
        </div>
      </div>
      {loading ? (
        <div className="payouts-loading">
          <div className="loading-spinner"></div>
          <p>Loading payout data...</p>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <div className="payouts-overview">
              <div className="payouts-metrics-grid">
                <div className="payouts-metric-card">
                  <div className="metric-icon blue">
                    <LucideIcons.IndianRupee size={24} />
                  </div>
                  <div className="metric-content">
                    <h3>Total Payouts</h3>
                    <p className="metric-value">{summary?.totals?.totalCount ?? payoutMetrics.totalPayouts}</p>
                    <span className="metric-label">All time payouts</span>
                  </div>
                </div>
                
                <div className="payouts-metric-card">
                  <div className="metric-icon green">
                    <LucideIcons.CheckCircle size={24} />
                  </div>
                  <div className="metric-content">
                    <h3>Paid Amount</h3>
                    <p className="metric-value">{fmt(summary?.totals?.paidAmount ?? payoutMetrics.paidAmount)}</p>
                    <span className="metric-label">Successfully processed</span>
                  </div>
                </div>
                
                <div className="payouts-metric-card">
                  <div className="metric-icon orange">
                    <LucideIcons.Clock size={24} />
                  </div>
                  <div className="metric-content">
                    <h3>Pending Amount</h3>
                    <p className="metric-value">{fmt(summary?.totals?.pendingAmount ?? payoutMetrics.pendingAmount)}</p>
                    <span className="metric-label">Awaiting processing</span>
                  </div>
                </div>
                
                <div className="payouts-metric-card">
                  <div className="metric-icon purple">
                    <LucideIcons.Users size={24} />
                  </div>
                  <div className="metric-content">
                    <h3>Pending Payouts</h3>
                    <p className="metric-value">{summary?.totals?.pendingCount ?? payoutMetrics.pendingPayouts}</p>
                    <span className="metric-label">Require attention</span>
                  </div>
                </div>
              </div>

              <div className="payouts-overview-content">
                <div className="overview-section">
                  <div className="section-header">
                    <h2>Recent Payouts</h2>
                    <button className="btn-secondary" onClick={() => setActiveTab('payouts')}>View All</button>
                  </div>
                  <div className="payouts-preview">
                    {payouts.slice(0, 5).map(payout => (
                      <div key={payout.id} className="payout-item">
                        <div className="payout-info">
                          <h4>{payout.vendor_name || payout.vendor_id}</h4>
                          <p>{payout.vendor_email} • {payout.method?.replace('_', ' ')}</p>
                        </div>
                        <div className="payout-details">
                          <span className="payout-amount">{fmt(payout.amount)}</span>
                          <div className="payout-status">
                            <span className={`status-badge ${payout.status}`}>
                              {payout.status} <StatusIcon status={payout.status} />
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {payouts.length === 0 && (
                      <div className="empty-state">
                        <LucideIcons.IndianRupee size={48} />
                        <p>No payouts found</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="overview-section">
                  <div className="section-header">
                    <h2>Pending Actions</h2>
                    <button className="btn-secondary" onClick={() => setActiveTab('pending')}>Manage</button>
                  </div>
                  <div className="pending-actions-preview">
                    {payouts.filter(p => p.status === 'pending').slice(0, 3).map(payout => (
                      <div key={payout.id} className="pending-action-item">
                        <div className="action-info">
                          <h4>{payout.vendor_name || payout.vendor_id}</h4>
                          <p>{fmt(payout.amount)} • {new Date(payout.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="action-buttons">
                          <button className="btn-approve" onClick={() => markPaid(payout.id)}>
                            <LucideIcons.Check size={16} />
                            Mark Paid
                          </button>
                        </div>
                      </div>
                    ))}
                    {payouts.filter(p => p.status === 'pending').length === 0 && (
                      <div className="empty-state">
                        <LucideIcons.CheckCircle size={48} />
                        <p>No pending payouts</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {(activeTab === 'payouts' || activeTab === 'pending' || activeTab === 'reports') && (
            <div className="payouts-main">
              <div className="payouts-filters">
                <div className="filters-header">
                  <h2>Payout Management</h2>
                  <div className="filters-actions">
                    <button 
                      className={`filter-toggle ${showFilters ? 'active' : ''}`}
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <LucideIcons.Filter size={16} />
                      Filters
                    </button>
                    <button className="btn-secondary" onClick={async () => {
                      try {
                        const qp = new URLSearchParams({ status, vendor, date_from: dateFrom, date_to: dateTo }).toString();
                        const adminKey = getApiKey();
                        const res = await fetch(`${API_BASE_URL}/api/admin/payouts/export?${qp}`, { headers: { 'x-admin-key': adminKey } });
                        if (!res.ok) { const e = await res.json(); alert(e.error || 'Export failed'); return; }
                        const blob = await res.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'payouts.csv';
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (e) { console.error('Export CSV error:', e); }
                    }}>
                      <LucideIcons.Download size={16} />
                      Export CSV
                    </button>
                  </div>
                </div>

                {showFilters && (
                  <div className="filters-content">
                    <div className="filters-grid">
                      <div className="filter-group">
                        <label className="filter-label">
                          <LucideIcons.Filter size={16} />
                          Status
                        </label>
                        <select 
                          className="filter-select"
                          value={status} 
                          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                        >
                          <option value="">All Status</option>
                          <option value="pending">Pending</option>
                          <option value="paid">Paid</option>
                          <option value="failed">Failed</option>
                        </select>
                      </div>

                      <div className="filter-group">
                        <label className="filter-label">
                          <LucideIcons.Search size={16} />
                          Vendor
                        </label>
                        <input 
                          className="filter-input"
                          placeholder="Vendor name/email/id" 
                          value={vendor} 
                          onChange={(e) => { setVendor(e.target.value); setPage(1); }} 
                        />
                      </div>

                      <div className="filter-group">
                        <label className="filter-label">
                          <LucideIcons.Calendar size={16} />
                          From Date
                        </label>
                        <input 
                          type="date" 
                          className="filter-input"
                          value={dateFrom} 
                          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} 
                        />
                      </div>

                      <div className="filter-group">
                        <label className="filter-label">
                          <LucideIcons.Calendar size={16} />
                          To Date
                        </label>
                        <input 
                          type="date" 
                          className="filter-input"
                          value={dateTo} 
                          onChange={(e) => { setDateTo(e.target.value); setPage(1); }} 
                        />
                      </div>
                    </div>

                    <div className="filters-actions">
                      <button className="btn-primary" onClick={() => load({ page: 1 })}>
                        <LucideIcons.Search size={16} />
                        Apply Filters
                      </button>
                      <button className="btn-secondary" onClick={() => {
                        setStatus(''); setVendor(''); setDateFrom(''); setDateTo(''); setPage(1); 
                        load({ status: '', vendor: '', dateFrom: '', dateTo: '', page: 1 });
                      }}>
                        <LucideIcons.RotateCcw size={16} />
                        Reset
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="payouts-table-container">
                <div className="table-header">
                  <div className="table-info">
                    <span>Showing {payouts.length} of {total} payouts</span>
                  </div>
                  <div className="table-actions">
                    <div className="bulk-actions">
                      <input type="checkbox" checked={selectedIds.length === payouts.length && payouts.length > 0} onChange={(e) => toggleSelectAll(e.target.checked)} />
                      <button className="btn-secondary" disabled={selectedIds.length === 0} onClick={() => bulkMark('paid')}>
                        <LucideIcons.Check size={14} /> Mark Paid
                      </button>
                      <button className="btn-secondary" disabled={selectedIds.length === 0} onClick={() => bulkMark('failed')}>
                        <LucideIcons.X size={14} /> Mark Failed
                      </button>
                    </div>
                    <select 
                      className="page-size-select"
                      value={limit} 
                      onChange={(e) => { const nl = parseInt(e.target.value); setLimit(nl); setPage(1); load({ limit: nl, page: 1 }); }}
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>
                </div>

                <div className="payouts-grid">
                  {payouts.map(payout => (
                    <div key={payout.id} className="payout-card">
                      <div className="payout-header">
                        <div className="payout-title">
                          <input type="checkbox" checked={selectedIds.includes(payout.id)} onChange={(e) => toggleSelect(payout.id, e.target.checked)} />
                          <h4>#{payout.id}</h4>
                          <span className="payout-vendor">{payout.vendor_name || payout.vendor_id}</span>
                        </div>
                        <div className="payout-status">
                          <span className={`status-badge ${payout.status}`}>
                            {payout.status} <StatusIcon status={payout.status} />
                          </span>
                        </div>
                      </div>
                      
                      <div className="payout-details">
                        <div className="detail-item">
                          <span className="detail-label">Amount</span>
                          <span className="detail-value amount">{fmt(payout.amount)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Method</span>
                          <span className="detail-value">{payout.method?.replace('_', ' ')}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Email</span>
                          <span className="detail-value">{payout.vendor_email || '-'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Created</span>
                          <span className="detail-value">{new Date(payout.created_at).toLocaleDateString()}</span>
                        </div>
                        {payout.paid_at && (
                          <div className="detail-item">
                            <span className="detail-label">Paid At</span>
                            <span className="detail-value">{new Date(payout.paid_at).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>

                      <div className="payout-reference">
                        {payout.status === 'pending' ? (
                          <div className="reference-input">
                            <input
                              type="text"
                              placeholder="Reference number"
                              value={referenceMap[payout.id] || ''}
                              onChange={(e) => setReferenceMap(prev => ({ ...prev, [payout.id]: e.target.value }))}
                            />
                          </div>
                        ) : (
                          <div className="reference-display">
                            <span className="reference-label">Reference:</span>
                            <span className="reference-value">{payout.reference || '-'}</span>
                          </div>
                        )}
                      </div>

                      <div className="payout-actions">
                        {payout.status === 'pending' && (
                          <button 
                            className="btn-approve" 
                            onClick={() => markPaid(payout.id)} 
                            disabled={marking === payout.id}
                          >
                            {marking === payout.id ? (
                              <>
                                <div className="loading-spinner small"></div>
                                Processing...
                              </>
                            ) : (
                              <>
                                <LucideIcons.Check size={16} />
                                Mark Paid
                              </>
                            )}
                          </button>
                        )}
                        <button className="action-btn view" onClick={() => openDetails(payout.id)}>
                          <LucideIcons.Eye size={16} />
                        </button>
                        <button className="action-btn edit">
                          <LucideIcons.Edit size={16} />
                        </button>
                        <button className="action-btn download" title="Download receipt" onClick={() => downloadReceipt(payout.id)}>
                          <LucideIcons.Download size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {payouts.length === 0 && (
                    <div className="empty-state">
                      <LucideIcons.IndianRupee size={48} />
                      <h3>No payouts found</h3>
                      <p>No payouts match your current filters</p>
                    </div>
                  )}
                </div>

                <div className="pagination">
                  <div className="pagination-info">
                    <span>Page {page} of {Math.max(1, Math.ceil(total / limit))}</span>
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pagination-btn" 
                      disabled={page <= 1} 
                      onClick={() => { const np = page - 1; setPage(np); load({ page: np }); }}
                    >
                      <LucideIcons.ChevronLeft size={16} />
                      Previous
                    </button>
                    <button 
                      className="pagination-btn" 
                      disabled={page >= Math.ceil(total / limit)} 
                      onClick={() => { const np = page + 1; setPage(np); load({ page: np }); }}
                    >
                      Next
                      <LucideIcons.ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {showDetails && (
        <div className="modal-backdrop" onClick={() => setShowDetails(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Payout Details #{details?.id}</h3>
              <button className="icon-btn" onClick={() => setShowDetails(false)}><LucideIcons.X size={16} /></button>
            </div>
            <div className="modal-content">
              <div className="details-grid">
                <div><strong>Vendor</strong><div>{details?.vendor_name} (#{details?.vendor_id})</div></div>
                <div><strong>Amount</strong><div>{fmt(details?.amount)}</div></div>
                <div><strong>Status</strong><div>{details?.status}</div></div>
                <div><strong>Method</strong><div>{details?.method?.replace('_',' ')}</div></div>
                <div><strong>Reference</strong><div>{details?.reference || '-'}</div></div>
                <div><strong>Requested</strong><div>{details?.created_at ? new Date(details.created_at).toLocaleString() : '-'}</div></div>
                <div><strong>Processed</strong><div>{details?.paid_at ? new Date(details.paid_at).toLocaleString() : '-'}</div></div>
              </div>
              <div className="logs-section">
                <h4>Audit Logs</h4>
                <div className="logs-list">
                  {(logs || []).map(l => (
                    <div key={l.id} className="log-item">
                      <div className="log-meta">{new Date(l.created_at).toLocaleString()} • {l.admin_identifier}</div>
                      <div className="log-body"><strong>{l.action}</strong>{l.notes ? ` — ${l.notes}` : ''}</div>
                    </div>
                  ))}
                  {(logs || []).length === 0 && <div className="empty-state small"><p>No logs</p></div>}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetails(false)}>Close</button>
              {details?.id && <button className="btn-primary" onClick={() => downloadReceipt(details.id)}><LucideIcons.Download size={14} /> Receipt</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayouts;


